import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { CompassLogo } from "@/components/CompassLogo";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api, getApiErrorMessage } from "@/lib/api";
import { TagInput } from "@/components/TagInput";
import { LanguageSelect } from "@/components/LanguageSelect";
import { LegalConsentText } from "@/components/LegalConsentText";

const TODAY = new Date().toISOString().split("T")[0];

const WELCOME_IMG =
  "https://images.unsplash.com/photo-1751199199992-b32cefa81c72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwY2FmZSUyMHdhcm18ZW58MHx8fHwxNzg0NjY2NDc4fDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { lang, toggle } = useLang();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup | forgot
  const [loading, setLoading] = useState(false);
  const A = UI.auth;

  const [form, setForm] = useState({
    prenom: "", email: "", password: "",
    date_arrivee_en_france: "", ecole: "", ville: "", quartier: "",
    langues_parlees: [], centres_interet: [], consentement_rgpd: false, cgu_accepted: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/reperes";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !form.consentement_rgpd) {
      toast.error(t(A.rgpdRequired, lang));
      return;
    }
    if (mode === "signup" && !form.cgu_accepted) {
      toast.error(t(UI.legal.required, lang));
      return;
    }
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success(t(A.resetSent, lang));
        setMode("login");
      } else if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data);
      } else {
        const { data } = await api.post("/auth/register", form);
        setUser(data);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-md mx-auto min-h-screen flex flex-col">
        {/* Hero */}
        <div className="relative h-52 shrink-0 overflow-hidden">
          <img src={WELCOME_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-paper via-paper/40 to-ink/20" />
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="absolute top-4 right-4 rounded-full border border-white/40 bg-white/80 backdrop-blur px-3 py-1.5 text-sm font-bold"
          >
            {lang.toUpperCase()}
          </button>
          <div className="absolute bottom-4 left-5 flex items-center gap-2.5">
            <CompassLogo size={40} />
            <div>
              <div className="font-display text-2xl font-extrabold tracking-tight leading-none">Boussole</div>
              <div className="text-xs font-medium text-ink/70 mt-1">{t(UI.tagline, lang)}</div>
            </div>
          </div>
        </div>

        <div className="flex-1 px-5 pb-10 pt-6">
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            {mode === "forgot" ? t(A.forgot, lang) : mode === "signup" ? t(A.signup, lang) : t(A.welcome, lang)}
          </h1>
          <p className="text-sm text-ink/60 mt-1 mb-6">{t(A.subtitle, lang)}</p>

          {mode !== "forgot" && (
            <>
              <button
                data-testid="google-login-btn"
                onClick={googleLogin}
                className="w-full flex items-center justify-center gap-3 rounded-2xl border border-line bg-white py-3.5 font-semibold hover:border-amber transition-colors"
              >
                <GoogleIcon />
                {t(A.google, lang)}
              </button>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-line" />
                <span className="text-xs text-ink/40 font-medium">{t(A.or, lang)}</span>
                <div className="flex-1 h-px bg-line" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <Input testId="signup-prenom" label={t(A.prenom, lang)} value={form.prenom} onChange={(v) => set("prenom", v)} maxLength={60} required />
            )}
            <Input testId="auth-email" type="email" label={t(A.email, lang)} value={form.email} onChange={(v) => set("email", v)} maxLength={120} required />
            {mode !== "forgot" && (
              <Input testId="auth-password" type="password" label={t(A.password, lang)} value={form.password} onChange={(v) => set("password", v)} minLength={6} maxLength={128} required />
            )}

            {mode === "signup" && (
              <div className="space-y-3 pt-1">
                <Input testId="signup-date" type="date" max={TODAY} label={t(A.dateArrivee, lang)} value={form.date_arrivee_en_france} onChange={(v) => set("date_arrivee_en_france", v)} />
                <Input testId="signup-ecole" label={t(A.ecole, lang)} value={form.ecole} onChange={(v) => set("ecole", v)} maxLength={120} />
                <div className="grid grid-cols-2 gap-3">
                  <Input testId="signup-ville" label={t(A.ville, lang)} value={form.ville} onChange={(v) => set("ville", v)} maxLength={60} />
                  <Input testId="signup-quartier" label={t(A.quartier, lang)} value={form.quartier} onChange={(v) => set("quartier", v)} maxLength={60} />
                </div>
                <FieldLabel>{t(A.langues, lang)}</FieldLabel>
                <LanguageSelect testId="signup-langues" value={form.langues_parlees} onChange={(v) => set("langues_parlees", v)} />
                <FieldLabel>{t(A.interets, lang)}</FieldLabel>
                <TagInput testId="signup-interets" value={form.centres_interet} onChange={(v) => set("centres_interet", v)} placeholder={t(A.addTag, lang)} />

                <label className="flex items-start gap-3 rounded-2xl border border-line bg-white p-3.5 cursor-pointer">
                  <input
                    data-testid="rgpd-checkbox"
                    type="checkbox"
                    checked={form.consentement_rgpd}
                    onChange={(e) => set("consentement_rgpd", e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-sage shrink-0"
                  />
                  <span className="text-xs text-ink/70 leading-relaxed">{t(A.rgpd, lang)}</span>
                </label>

                <label className="flex items-start gap-3 rounded-2xl border border-line bg-white p-3.5 cursor-pointer">
                  <input
                    data-testid="cgu-checkbox"
                    type="checkbox"
                    checked={form.cgu_accepted}
                    onChange={(e) => set("cgu_accepted", e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-sage shrink-0"
                  />
                  <span className="text-xs text-ink/70 leading-relaxed"><LegalConsentText /></span>
                </label>
              </div>
            )}

            {mode === "login" && (
              <button type="button" data-testid="forgot-link" onClick={() => setMode("forgot")} className="text-sm font-semibold text-sage hover:underline">
                {t(A.forgot, lang)}
              </button>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-amber text-ink font-bold py-3.5 hover:brightness-105 active:scale-[0.99] transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "forgot" ? t(A.resetSend, lang) : mode === "signup" ? t(A.signup, lang) : t(A.login, lang)}
            </button>
          </form>

          <div className="text-center mt-5 text-sm text-ink/60">
            {mode === "forgot" ? (
              <button data-testid="switch-login" onClick={() => setMode("login")} className="font-semibold text-ink hover:underline">
                {t(A.backLogin, lang)}
              </button>
            ) : mode === "login" ? (
              <>
                {t(A.noAccount, lang)}{" "}
                <button data-testid="switch-signup" onClick={() => setMode("signup")} className="font-semibold text-ink hover:underline">
                  {t(A.signup, lang)}
                </button>
              </>
            ) : (
              <>
                {t(A.hasAccount, lang)}{" "}
                <button data-testid="switch-login" onClick={() => setMode("login")} className="font-semibold text-ink hover:underline">
                  {t(A.login, lang)}
                </button>
              </>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-line flex items-center justify-center gap-4 text-xs text-ink/45">
            <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-ink underline">{t(UI.legal.terms, lang)}</Link>
            <span>·</span>
            <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-ink underline">{t(UI.legal.privacy, lang)}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <span className="block text-sm font-semibold mb-1 text-ink/80">{children}</span>;
}

function Input({ label, value, onChange, type = "text", required, testId, maxLength, minLength, max }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        data-testid={testId}
        type={type}
        required={required}
        maxLength={maxLength}
        minLength={minLength}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-line bg-white px-3.5 py-3 text-sm outline-none focus:border-amber transition-colors"
      />
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.2 13.3 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7C43.8 37.7 46.5 31.6 46.5 24.5z" />
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.7c-2 1.4-4.7 2.3-8.6 2.3-6.4 0-11.8-3.8-13.6-9.2l-7.9 6.1C6.4 42.6 14.6 48 24 48z" />
    </svg>
  );
}
